import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Building2, Phone, Printer, Mail, Globe, MapPin, Download, ClipboardList, FileText, CalendarCheck } from "lucide-react";
import { company, doddServices } from "../mock";
import { track } from "../api";
import useSeo from "../hooks/useSeo";

const Providers = () => {
  useSeo({
    title: "For County Boards, SSAs & Referral Professionals | Caregivers Alliance Homecare",
    description: "Provider information for Ohio County Boards, SSAs and referral professionals: DODD certified services, effective dates, referral routes and contact details for Caregivers Alliance Homecare LLC, Columbus, Ohio.",
    keywords: "DODD provider information Ohio, county board referral provider, SSA home care provider Columbus, provider RFP home care Ohio",
    path: "/providers",
  });
  useEffect(() => { track("professional_partner_inquiry", ""); }, []);

  return (
    <>
      <section className="brand-gradient text-white">
        <div className="mx-auto max-w-7xl px-4 py-14">
          <div className="inline-flex items-center gap-2 bg-white/15 rounded-full px-4 py-1.5 text-sm font-semibold mb-4">
            <Building2 className="h-4 w-4" /> Provider information
          </div>
          <h1 className="font-display font-extrabold text-4xl md:text-5xl max-w-3xl">For County Boards, SSAs &amp; Referral Professionals</h1>
        </div>
      </section>

      <section className="section bg-white">
        <div className="mx-auto max-w-7xl px-4 grid lg:grid-cols-2 gap-10 items-start">
          <div data-testid="provider-info-card" className="bg-[#F7FBFC] rounded-2xl border border-slate-100 p-8">
            <h2 className="font-display font-bold text-2xl text-[#0E2A33]">Caregivers Alliance Homecare LLC</h2>
            <ul className="mt-5 space-y-3 text-slate-700">
              <li className="flex items-start gap-3"><MapPin className="h-5 w-5 text-[#0E5A72] shrink-0 mt-0.5" /> {company.address}, {company.city}</li>
              <li className="flex items-center gap-3"><Phone className="h-5 w-5 text-[#0E5A72]" /> <a href={`tel:${company.phoneRaw}`} className="hover:underline">{company.phone}</a></li>
              <li className="flex items-center gap-3"><Printer className="h-5 w-5 text-[#0E5A72]" /> Fax {company.fax}</li>
              <li className="flex items-center gap-3"><Mail className="h-5 w-5 text-[#0E5A72]" /> <a href={`mailto:${company.email}`} className="hover:underline">{company.email}</a></li>
              <li className="flex items-center gap-3"><Globe className="h-5 w-5 text-[#0E5A72]" /> https://myohcare.com/</li>
            </ul>
            <div className="mt-6 pt-5 border-t border-slate-200">
              <div className="font-semibold text-[#0E2A33] mb-2">DODD Certified Services</div>
              <ul className="text-sm text-slate-600 space-y-1">
                {doddServices.map((s) => <li key={s}>• {s}</li>)}
              </ul>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="font-display font-bold text-xl text-[#0E2A33]">How to work with us</h3>
            <Link to="/refer" className="block">
              <Button data-testid="providers-refer-btn" className="w-full justify-start rounded-xl bg-[#0E5A72] hover:bg-[#0A4557] text-white h-14 text-base font-semibold">
                <ClipboardList className="mr-3 h-5 w-5" /> SEND A REFERRAL
              </Button>
            </Link>
            <a href={`mailto:${company.email}?subject=RFP%20%2F%20Provider%20Request%20—%20Caregivers%20Alliance%20Homecare`} className="block">
              <Button className="w-full justify-start rounded-xl bg-[#2E9E6A] hover:bg-[#23875A] text-white h-14 text-base font-semibold">
                <FileText className="mr-3 h-5 w-5" /> SEND AN RFP / PROVIDER REQUEST
              </Button>
            </a>
            <a href={company.calendly} target="_blank" rel="noopener noreferrer" className="block">
              <Button data-testid="providers-book-btn" className="w-full justify-start rounded-xl bg-[#D97706] hover:bg-[#B45F05] text-white h-14 text-base font-semibold">
                <CalendarCheck className="mr-3 h-5 w-5" /> BOOK A 15-MINUTE INTRO CALL
              </Button>
            </a>
            <Link to="/contact" className="block">
              <Button variant="outline" className="w-full justify-start rounded-xl border-[#0E5A72] text-[#0E5A72] h-14 text-base font-semibold">
                <Phone className="mr-3 h-5 w-5" /> CHECK SERVICE AVAILABILITY
              </Button>
            </Link>
            <Link to="/provider-sheet" className="block">
              <Button data-testid="providers-download-btn" variant="outline" className="w-full justify-start rounded-xl border-slate-300 text-slate-700 h-14 text-base font-semibold">
                <Download className="mr-3 h-5 w-5" /> DOWNLOAD PROVIDER INFORMATION
              </Button>
            </Link>
            <p className="text-xs text-slate-400">
              Referral coverage is available statewide. Actual service availability depends on staffing, location,
              schedule, and individual support requirements.
            </p>
          </div>
        </div>
      </section>
    </>
  );
};

export default Providers;
